import React, {useCallback, useMemo, useState} from 'react';

import {Typography} from '@mui/material';
import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';

import PoweredByModak from '@/app/shared-components/PoweredByModak/PoweredByModak';
import logError from '@/utils/errors/logError';

export interface PaymentLinkHeaderProps {
  name: string;
  gif?: string;
  note?: string;
  amount?: number;
  isLink?: boolean;
  onCustomAmountClick?: () => void;
  paymentLinkId?: string;
}

const PaymentLinkHeader: React.FC<PaymentLinkHeaderProps> = ({
  name,
  gif,
  note,
  amount,
  paymentLinkId,
}) => {
  const {t} = useTranslation();
  const [imgError, setImgError] = useState(false);

  const handleImgError = useCallback(() => {
    setImgError(true);
    const error = new Error('Gif Image failed to load');
    logError(error, {
      fileName: 'PaymentLinkHeader.tsx',
      functionName: 'handleImgError',
      customTitle: 'Payment Link error loading gif',
      extraTags: {
        payment_link_id: paymentLinkId,
        img_src: gif,
      },
    });
  }, [gif, paymentLinkId]);

  const showImage = useMemo(() => {
    return gif && !imgError;
  }, [gif, imgError]);

  return (
    <Box className="payment-link-valid-header">
      <Box className="payment-link-valid-info">
        <Box className="payment-link-valid-avatar-and-security-container">
          {showImage ? (
            <img
              src={gif}
              alt="payment-link-gif"
              className="payment-link-valid-gif"
              style={{height: 72, borderRadius: 4}}
              onError={handleImgError}
            />
          ) : (
            <Box
              id="PaymentLinkHeader_avatarBox"
              className="payment-link-valid-avatar">
              <Typography
                id="PaymentLinkHeader_avatarText"
                className="payment-link-valid-avatar-text">
                {name.charAt(0)}
              </Typography>
            </Box>
          )}
          <PoweredByModak />
        </Box>
        {name && (
          <Typography className="payment-link-valid-recipient">
            {t('payment_link.pay', {name})}
          </Typography>
        )}
        {note && (
          <Typography className="payment-link-valid-note-text">
            {t('payment_link.note_wrapped', {note})}
          </Typography>
        )}
        {!!amount && (
          <Typography className="payment-link-valid-amount">
            ${amount.toFixed(2)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PaymentLinkHeader;
